import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiErrorHandler } from '@/utils/apiErrorHandler';
import { API_BASE_URL } from '@/config';
import { queryKeys } from '@/lib/queryKeys';
import { useImportProgressStore } from '@/stores/importProgressStore';
import type {
  ImportHistoryResponse,
  ImportJobRecordsResponse,
  GeocodingStatsResponse,
  RequestUploadRequest,
  RetryImportRequest,
  StagingTableInfo,
  VoterImportCancelResponse,
  VoterImportProgressEvent,
  VoterImportRegeocodeResponse,
  VoterImportRetryResponse,
  VoterImportStagingRowUpdateResponse,
  VoterImportStartedResponse,
  VoterImportUploadResponse,
} from '@signalvote/shared';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch import job history with pagination
 */
export function useImportHistory(params?: { page?: number; pageSize?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
  const qs = searchParams.toString();

  return useQuery({
    queryKey: queryKeys.voterImport.history(params),
    queryFn: (): Promise<ImportHistoryResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(
        `${API_BASE_URL}/api/voterimport/history${qs ? `?${qs}` : ''}`
      ),
  });
}

/**
 * Fetch campaign-wide geocoding accuracy stats
 */
export function useGeocodingStats() {
  return useQuery({
    queryKey: queryKeys.voterImport.geocodingStats(),
    queryFn: (): Promise<GeocodingStatsResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(
        `${API_BASE_URL}/api/voterimport/geocoding-stats`
      ),
  });
}

/**
 * Fetch the columns of an existing staging table so its mapping can be redone.
 * Keyed on the table name so a response for a table the user has navigated away
 * from is never applied to the one they are looking at now.
 */
export function useStagingTableInfo(stagingTableName?: string) {
  return useQuery({
    queryKey: queryKeys.voterImport.stagingTable(stagingTableName ?? ''),
    queryFn: (): Promise<StagingTableInfo> =>
      ApiErrorHandler.makeAuthenticatedRequest(
        `${API_BASE_URL}/api/voterimport/staging-tables/${stagingTableName}`
      ),
    enabled: !!stagingTableName,
  });
}

/**
 * Fetch individual records for an import job with optional filters
 */
export function useImportJobRecords(
  jobId: string,
  params?: { status?: string; page?: number; pageSize?: number }
) {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
  const qs = searchParams.toString();

  return useQuery({
    queryKey: queryKeys.voterImport.records(jobId, params as Record<string, unknown>),
    queryFn: (): Promise<ImportJobRecordsResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(
        `${API_BASE_URL}/api/voterimport/${jobId}/records${qs ? `?${qs}` : ''}`
      ),
    enabled: !!jobId,
  });
}

// ============================================================================
// SSE Hooks
// ============================================================================

/**
 * Subscribe to real-time import progress via Server-Sent Events.
 * Reconnects on connection drop (3s delay), but NOT after terminal
 * states (Completed/Failed/Cancelled) or when backend reports Idle (no active job).
 *
 * @param reconnectKey — change this value to force a fresh SSE connection
 *   (e.g., after kicking off a new import).
 * @param enabled — false for a user the API refuses voter import, so no stream is opened.
 */
export function useImportProgress(reconnectKey = 0, enabled = true) {
  const [progress, setProgress] = useState<VoterImportProgressEvent | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let terminated = false;
    let retryCount = 0;
    const MAX_RETRIES = 5;

    async function connect() {
      if (terminated) return;

      try {
        const response = await ApiErrorHandler.makeAuthenticatedRequestRaw(
          `${API_BASE_URL}/api/voterimport/progress-stream`,
          { signal: controller.signal }
        );

        if (!response.ok || !response.body) return;

        setConnected(true);
        retryCount = 0; // Reset on successful connection
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6)) as VoterImportProgressEvent;
                // Backend sends {"status":"Idle"} when no active job exists
                if (data.status === 'Idle') {
                  terminated = true;
                  break;
                }
                setProgress(data);
                // Don't reconnect after terminal states
                if (data.status === 'Completed' || data.status === 'Failed' || data.status === 'Cancelled') {
                  terminated = true;
                }
              } catch { /* ignore parse errors */ }
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError' && !terminated) {
          retryCount++;
          if (retryCount <= MAX_RETRIES) {
            // Exponential backoff: 3s, 6s, 12s, 24s, 48s
            const delay = 3000 * Math.pow(2, retryCount - 1);
            setTimeout(connect, delay);
          }
        }
      } finally {
        setConnected(false);
      }
    }

    connect();
    return () => controller.abort();
  }, [reconnectKey, enabled]);

  return { progress, connected };
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Request a presigned upload URL for a voter import file
 */
export function useRequestUpload() {
  return useMutation({
    mutationFn: (fileName: string): Promise<VoterImportUploadResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(
        `${API_BASE_URL}/api/voterimport/request-upload`,
        {
          method: 'POST',
          body: JSON.stringify({ fileName } satisfies RequestUploadRequest),
        }
      ),
  });
}

/**
 * Start an import job from a file already uploaded to Spaces
 */
export function useImportFromSpaces() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: {
      fileKey: string;
      fileName: string;
      delimiter: string;
      mapping: Record<string, unknown>;
    }): Promise<VoterImportStartedResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(
        `${API_BASE_URL}/api/voterimport/import`,
        {
          method: 'POST',
          body: JSON.stringify(request),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.voterImport.history() });
    },
  });
}

/**
 * Cancel an in-progress import job
 */
export function useCancelImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string): Promise<VoterImportCancelResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(
        `${API_BASE_URL}/api/voterimport/${jobId}/cancel`,
        { method: 'POST' }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.voterImport.history() });
    },
  });
}

/**
 * Retry failed rows in an import job
 */
export function useRetryImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobId, rowNumbers }: { jobId: string; rowNumbers: number[] }): Promise<VoterImportRetryResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(
        `${API_BASE_URL}/api/voterimport/${jobId}/retry`,
        {
          method: 'POST',
          body: JSON.stringify({ rowNumbers } satisfies RetryImportRequest),
        }
      ),
    onSuccess: (_data, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.voterImport.records(jobId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.voterImport.history() });
    },
  });
}

/**
 * Update a single staging row before import
 */
export function useUpdateStagingRow() {
  return useMutation({
    mutationFn: ({
      tableName,
      rowId,
      data,
    }: {
      tableName: string;
      rowId: number;
      data: Record<string, string>;
    }): Promise<VoterImportStagingRowUpdateResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(
        `${API_BASE_URL}/api/voterimport/staging-rows/${tableName}/${rowId}`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      ),
  });
}

/**
 * Trigger re-geocoding of low-accuracy voters
 */
export function useRegeocodeVoters() {
  const queryClient = useQueryClient();
  const startRegeocode = useImportProgressStore((s) => s.startRegeocode);

  return useMutation({
    mutationFn: (): Promise<VoterImportRegeocodeResponse> =>
      ApiErrorHandler.makeAuthenticatedRequest(
        `${API_BASE_URL}/api/voterimport/regeocode`,
        {
          method: 'POST',
          body: JSON.stringify({}),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.voterImport.history() });
      queryClient.invalidateQueries({ queryKey: queryKeys.voterImport.geocodingStats() });
      startRegeocode();
    },
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Returns a function that downloads import errors as a CSV file.
 * Not a hook - call the returned function to trigger the download.
 */
export function createExportImportErrors(jobId: string) {
  const exportErrors = async () => {
    const response = await ApiErrorHandler.makeAuthenticatedRequestRaw(
      `${API_BASE_URL}/api/voterimport/${jobId}/export-errors`
    );

    if (!response.ok) {
      throw new Error('Failed to export errors');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `import-errors-${jobId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return exportErrors;
}
